/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: partner-email-endpoints-searchview-component.ts
 *
 * Created by randysorensen on 9/28/17.
 */

'use strict';

function PartnerEmailEndpointsSearchViewController (this: JS5Object, _: JS5Object, $location: JS5Object,
		uiGridConstants: JS5Object, ModalService: JS5Object, NavbarUtilService: JS5Object,
		NotificationService: JS5Object, PartnerEditService: JS5Object) {

	//=================//
	// Lifecycle Hooks //
	//=================//

	this.$onInit = (): void => {
		this.loadingIndicator = {
			backdrop: true,
			show: false,
			title: 'Working',
			message: 'Please wait...'
		};
		this.showHidden = false;

		// Navbar search button click handler
		NavbarUtilService.viewSearchFunction = queryForGridData;

		queryForGridData();
	};


	//======//
	// Data //
	//======//

	let queryForGridData = (): void => {
		if (this.canRead) {
			this.loadingIndicator.show = true;
			PartnerEditService
				.getEmailEndpointSearchViewData(this.showHidden)
				.then((data: JS5Object): void => {
					this.partnerObject = data.partnerObject;
					this.gridOptions.data = data.emailEndpoints;
					this.searchText = $location.search().searchText || null;

					this.loadingIndicator.show = false;
				});
		}
	};


	//=========//
	// Actions //
	//=========//

	let newEmailEndpoint = (): void => {
		$location.url(
			'/system/partners/edit/' + this.partnerObject.id +
			'/emailendpoints/edit/-1'
		);
	};

	let editEmailEndpoint = (endpoint: JS5Object): void => {
		if (endpoint.hidden) {
			NotificationService.createWarningNotification('"' + endpoint.name + '" is hidden');
		} else {
			$location.url(
				'/system/partners/edit/' + this.partnerObject.id +
				'/emailendpoints/edit/' + endpoint.id);
		}
	};

	let cloneSelectedEmailEndpoint = (): void => {
		this.loadingIndicator.show = true;

		let endpoint = getSelectedEmailEndpoint();
		PartnerEditService.cloneEmailEndpoint(endpoint.id)
			.then(() => {
				this.gridApi.selection.clearSelectedRows();
				queryForGridData();

				// LI should already be hidden by
				// queryForGridData()
				this.loadingIndicator.show = false;
			});
	};

	let toggleSelectedEmailEndpointVisibility = (): void => {
		this.loadingIndicator.show = true;

		let endpoint = getSelectedEmailEndpoint();
		PartnerEditService.toggleEmailEndpointVisibility(endpoint.id)
			.then(() => {
				this.gridApi.selection.clearSelectedRows();
				queryForGridData();

				// LI should already be hidden by
				// queryForGridData()
				this.loadingIndicator.show = false;
			})
	};

	let showConfirmationModal = (action: string, handler: JS5Function): void => {
		let capitalizedAction: string = action[0].toUpperCase() + action.slice(1);
		ModalService.showMessageModal({
				messageText: 'Are you sure you want to ' + action +
				' "' + getSelectedEmailEndpoint().name + '"?',
				title: capitalizedAction + ' Partner Email Endpoint',
				windowClass: 'confirm-modal-sm',
				showOK: true,
				okText: 'Yes',
				pullCancelLeft: true
			},
			function (): void { handler(); },
			function (): void {}
		);
	};


	//======//
	// Grid //
	//======//

	let gridColDefs = [
		{
			field: 'name',
			type: 'string',
			width: '15%',
			sort: {
				direction: uiGridConstants.ASC,
				priority: 0,
			},
			displayName: 'Name',
		},
		{
			field: 'emailTemplate.name',
			type: 'string',
			width: '15%',
			sort: {
				direction: uiGridConstants.ASC,
				priority: 1,
			},
			displayName: 'Email Template',
		},
		{
			field: 'subjectExpression',
			type: 'string',
			width: '*',
			sort: {
				direction: uiGridConstants.ASC,
				priority: 3,
			},
			displayName: 'Subject Expression',
		},
		{
			field: 'deliveryTemplates',
			type: 'string',
			width: '*',
			sort: {
				direction: uiGridConstants.ASC,
				priority: 2,
			},
			cellTemplate: 'system/partner/editor/components/view/emailEndpoints/searchView/gridTemplates/delivery-template-list-cell-template.html',
			displayName: 'Delivery Templates',
		},
		{
			field: 'recipients.length',
			type: 'number',
			width: 125,
			sort: {
				direction: uiGridConstants.ASC,
				priority: 4,
			},
			displayName: '# of Recipients',
		}
	];

	this.gridOptions = {
		rowSelection: true,
		enableRowHeaderSelection: false,
		enableSelectAll: false,
		enableColumnResizing: true,
		enablePaginationControls: false,
		enableSorting: true,
		enableColumnMenus: false,
		enableFullRowSelection: true,
		enableHorizontalScrollbar: uiGridConstants.ALWAYS,
		enableVerticalScrollbar: uiGridConstants.ALWAYS,
		multiSelect: false,
		columnDefs: gridColDefs,
		onRegisterApi: (gridApi: JS5Object) => {
			this.gridApi = gridApi;
			this.gridApi.grid.registerRowsProcessor(processGridFilter, 200);
			filterGrid(this.searchText);
		},
		rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
		data: []
	};

	this.onRowDblClick = (row: JS5Object): void => {
		editEmailEndpoint(row.entity);
	};

	let filterGrid = (searchText: JS5Object): void => {
		if (searchText !== null) {
			this.searchText = searchText;
			this.gridApi.grid.refresh();
		}
	};

	let processGridFilter = (renderableRows: Array<JS5Object>): Array<JS5Object> => {
		_.forEach(renderableRows, (row: JS5Object): void => {
			row.visible = (this.searchText === null)?
				true:
				_.includes(row.entity.name.toLowerCase(), this.searchText.toLowerCase());
		});
		return renderableRows;
	};

	let getSelectedEmailEndpoint = () => {
		return this.gridApi.selection.getSelectedRows()[0];
	};

	this.getDeliveryTemplateNames = (rowEntity: JS5Object): Array<String> => {
		return _.map(rowEntity.deliveryTemplates, 'template.name').sort();
	};


	//=========//
	// Toolbar //
	//=========//

	this.toolbarButtonDefs = [
		{
			label: 'New',
			enabled: () => this.canWrite,
			onClick: () => newEmailEndpoint(),
		},
		{
			label: 'Edit',
			enabled: () => this.gridApi.selection.getSelectedCount() === 1 && this.canRead,
			onClick: () => editEmailEndpoint(getSelectedEmailEndpoint()),
		},
		{
			label: 'Clone',
			enabled: () => this.gridApi.selection.getSelectedCount() === 1 && this.canWrite,
			onClick: () => showConfirmationModal('clone', cloneSelectedEmailEndpoint),
		},
		{
			label: 'Hide',
			enabled: () =>
				this.gridApi.selection.getSelectedCount() === 1 &&
				!getSelectedEmailEndpoint().hidden &&
				this.canWrite,
			onClick: () => showConfirmationModal('hide', toggleSelectedEmailEndpointVisibility),
		},
		{
			label: 'Unhide',
			enabled: () =>
				this.gridApi.selection.getSelectedCount() === 1 &&
				getSelectedEmailEndpoint().hidden &&
				this.canWrite,
			onClick: () => showConfirmationModal('hide', toggleSelectedEmailEndpointVisibility),
		}
	];

	this.toolbarCheckboxDefs = [
		{
			label: 'Show Hidden',
			onUpdate: (newValue: boolean) => {
				this.showHidden = newValue;
				queryForGridData();
			},
			isChecked: this.showHidden,
		},
	];
}

angular
.module('prism-ui.partner')
.component('partnerEmailEndpointsSearchView', {
	templateUrl: 'system/partner/editor/components/view/emailEndpoints/searchView/partner-email-endpoints-searchview-template.html',
	controller: PartnerEmailEndpointsSearchViewController,
	bindings: {
		canRead: '<',
		canWrite: '<',
	}
});
